import { useEffect, useState } from "react";
import {
    Calendar as CalIcon,
    TrendingUp,
    TrendingDown,
    DollarSign,
    BedDouble,
    Mail,
    Users,
    Tag,
    FileText,
    Activity,
    Clock,
    ArrowUpRight,
} from "lucide-react";
import { api } from "../lib/api";
import { useCurrency } from "../lib/CurrencyContext";

function StatCard({ icon: Icon, label, value, sub, change, accent = "navy", testId }) {
    const accents = {
        navy: "bg-[#0F3A59] text-white",
        gold: "bg-gradient-to-br from-[#CBA871] to-[#A88758] text-white",
        sand: "bg-[#F4EFE6] text-[#0F3A59]",
        coral: "bg-[#D17B6E] text-white",
    };
    const positive = (change ?? 0) > 0;
    const negative = (change ?? 0) < 0;
    return (
        <div data-testid={testId} className="bg-white rounded-2xl p-5 border border-[#E5E0D8] hover:shadow-[0_8px_30px_rgba(0,0,0,0.05)] transition-shadow">
            <div className="flex items-start justify-between gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accents[accent]}`}>
                    <Icon size={18} strokeWidth={1.6} />
                </div>
                {typeof change === "number" && (
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${
                        positive ? "bg-green-100 text-green-700" : negative ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"
                    }`}>
                        {positive ? <TrendingUp size={11} /> : negative ? <TrendingDown size={11} /> : null}
                        {Math.abs(change)}%
                    </span>
                )}
            </div>
            <div className="mt-4 font-serif text-3xl text-[#0F3A59] tracking-tight">{value}</div>
            <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/55 mt-1">{label}</div>
            {sub && <div className="text-[11px] text-[#1C1C1C]/65 font-light mt-2">{sub}</div>}
        </div>
    );
}

function BarChart({ data, format }) {
    const max = Math.max(...data.map((d) => d.revenue), 1);
    return (
        <div className="space-y-3" data-testid="revenue-chart">
            {data.map((d) => {
                const pct = (d.revenue / max) * 100;
                const label = (() => {
                    try {
                        const [y, m] = d.month.split("-");
                        return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("sq-AL", { month: "short", year: "2-digit" });
                    } catch {
                        return d.month;
                    }
                })();
                return (
                    <div key={d.month} className="flex items-center gap-3 text-xs">
                        <div className="w-16 text-[#1C1C1C]/65 shrink-0">{label}</div>
                        <div className="flex-1 h-7 bg-[#F4EFE6] rounded-md overflow-hidden relative">
                            <div
                                className="h-full bg-gradient-to-r from-[#0F3A59] to-[#1C5279] transition-all duration-700"
                                style={{ width: `${pct}%` }}
                            />
                            {d.revenue > 0 && (
                                <div className="absolute inset-0 flex items-center px-3 text-[#0F3A59] font-medium">
                                    {format(d.revenue)}
                                </div>
                            )}
                        </div>
                        <div className="w-10 text-right text-[#1C1C1C]/55">{d.bookings}</div>
                    </div>
                );
            })}
        </div>
    );
}

const STATUS_BADGES = {
    pending: { bg: "bg-yellow-100 text-yellow-700", label: "Pending" },
    confirmed: { bg: "bg-green-100 text-green-700", label: "Confirmed" },
    cancelled: { bg: "bg-red-100 text-red-700", label: "Cancelled" },
};

function formatRelative(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = Math.floor((now - d) / 1000);
        if (diff < 60) return "tani";
        if (diff < 3600) return `${Math.floor(diff / 60)}m`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
        return d.toLocaleDateString("sq-AL", { day: "numeric", month: "short" });
    } catch {
        return "";
    }
}

export default function AnalyticsAdmin() {
    const { format } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/admin/analytics")
            .then((r) => setData(r.data))
            .catch((e) => setError(e?.message || "Failed to load"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return <div className="text-center py-20 text-[#1C1C1C]/55 font-light">Loading analytics...</div>;
    }
    if (error) {
        return <div className="text-center py-20 text-red-600 font-light">Gabim: {error}</div>;
    }
    if (!data) return null;

    const { totals, this_month, monthly, top_rooms, top_promos, recent_bookings } = data;

    return (
        <div data-testid="analytics-dashboard" className="space-y-8">
            <div>
                <h2 className="font-serif text-2xl text-[#0F3A59] tracking-tight">Analytics Dashboard</h2>
                <p className="text-sm text-[#1C1C1C]/65 font-light mt-1">
                    Pamje e plotë e performancës së hotelit
                </p>
            </div>

            {/* Top stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={DollarSign}
                    accent="navy"
                    label="Të ardhura totale"
                    value={format(totals.revenue_eur)}
                    sub={`Mes. ${format(totals.avg_booking_value_eur)} / rezervim`}
                    testId="stat-revenue"
                />
                <StatCard
                    icon={CalIcon}
                    accent="gold"
                    label="Rezervime totale"
                    value={totals.bookings}
                    sub={`${totals.pending} në pritje · ${totals.confirmed} të konfirmuara`}
                    testId="stat-bookings"
                />
                <StatCard
                    icon={Activity}
                    accent="sand"
                    label="Ky muaj"
                    value={format(this_month.revenue_eur)}
                    change={this_month.revenue_change_pct}
                    sub={`${this_month.bookings} rezervime`}
                    testId="stat-this-month"
                />
                <StatCard
                    icon={BedDouble}
                    accent="coral"
                    label="Net total të rezervuara"
                    value={totals.total_nights}
                    sub={`Mes. ${(totals.total_nights / Math.max(1, totals.bookings - totals.cancelled)).toFixed(1)} net / rezervim`}
                    testId="stat-nights"
                />
            </div>

            {/* Revenue Chart */}
            <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8]">
                <div className="flex items-baseline justify-between mb-5">
                    <div>
                        <h3 className="font-serif text-xl text-[#0F3A59]">Të ardhura sipas muajit</h3>
                        <p className="text-xs text-[#1C1C1C]/55 font-light mt-1">12 muajt e fundit</p>
                    </div>
                    <div className="text-[11px] text-[#1C1C1C]/55 flex items-center gap-4">
                        <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded-sm bg-gradient-to-r from-[#0F3A59] to-[#1C5279]" />
                            Të ardhura
                        </span>
                        <span>· Rezervime (kolona djathtas)</span>
                    </div>
                </div>
                <BarChart data={monthly} format={format} />
            </div>

            {/* Two-column: Top rooms + Top promos */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8]">
                    <h3 className="font-serif text-xl text-[#0F3A59] mb-4 flex items-center gap-2">
                        <BedDouble size={18} strokeWidth={1.6} />
                        Dhomat më të kërkuara
                    </h3>
                    {top_rooms.length === 0 ? (
                        <p className="text-sm text-[#1C1C1C]/55 font-light py-4">Ende pa rezervime.</p>
                    ) : (
                        <ul className="divide-y divide-[#E5E0D8]/60" data-testid="top-rooms">
                            {top_rooms.map((r, i) => (
                                <li key={r.slug} className="py-3 flex items-center gap-3">
                                    <span className="font-serif text-2xl text-[#CBA871] w-8">{i + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-[#0F3A59] truncate">{r.title}</div>
                                        <div className="text-xs text-[#1C1C1C]/55 mt-0.5">
                                            {r.bookings} rezervime · {r.nights} net
                                        </div>
                                    </div>
                                    <div className="font-serif text-lg text-[#0F3A59]">{format(r.revenue)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-white rounded-2xl p-6 border border-[#E5E0D8]">
                    <h3 className="font-serif text-xl text-[#0F3A59] mb-1 flex items-center gap-2">
                        <Tag size={18} strokeWidth={1.6} />
                        Promo codes
                    </h3>
                    <p className="text-xs text-[#1C1C1C]/55 font-light mb-4">
                        Total i kursyer nga klientët: <span className="font-medium text-[#0F3A59]">{format(totals.promo_savings_eur)}</span>
                    </p>
                    {top_promos.length === 0 ? (
                        <p className="text-sm text-[#1C1C1C]/55 font-light py-4">Asnjë kod i krijuar.</p>
                    ) : (
                        <ul className="divide-y divide-[#E5E0D8]/60" data-testid="top-promos">
                            {top_promos.map((p) => (
                                <li key={p.code} className="py-3 flex items-center gap-3">
                                    <span className="font-mono text-xs px-2.5 py-1 rounded-full bg-[#0F3A59] text-white tracking-wider">
                                        {p.code}
                                    </span>
                                    <div className="flex-1 text-xs text-[#1C1C1C]/55">
                                        {p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""} përdorime
                                    </div>
                                    <span className="text-sm font-medium text-[#CBA871]">
                                        −{p.discount_type === "percentage" ? `${p.discount_value}%` : format(p.discount_value)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Recent bookings + content stats */}
            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-[#E5E0D8]">
                    <h3 className="font-serif text-xl text-[#0F3A59] mb-4 flex items-center gap-2">
                        <Clock size={18} strokeWidth={1.6} />
                        Rezervimet e fundit
                    </h3>
                    {recent_bookings.length === 0 ? (
                        <p className="text-sm text-[#1C1C1C]/55 font-light py-4">Ende pa rezervime.</p>
                    ) : (
                        <ul className="divide-y divide-[#E5E0D8]/60" data-testid="recent-bookings">
                            {recent_bookings.map((b) => {
                                const badge = STATUS_BADGES[b.status] || STATUS_BADGES.pending;
                                return (
                                    <li key={b.id} className="py-3 flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-[#0F3A59] truncate flex items-center gap-2">
                                                {b.name}
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge.bg}`}>{badge.label}</span>
                                            </div>
                                            <div className="text-xs text-[#1C1C1C]/55 mt-0.5">
                                                {b.check_in} → {b.check_out} · {b.nights} net · {b.guests} mysafirë
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-serif text-base text-[#0F3A59]">{format(b.total_price)}</div>
                                            <div className="text-[10px] text-[#1C1C1C]/45 mt-0.5">{formatRelative(b.created_at)}</div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="space-y-4">
                    <StatCard icon={Mail} accent="navy" label="Mesazhe" value={totals.messages} testId="stat-messages" />
                    <StatCard icon={Users} accent="gold" label="Newsletter" value={totals.subscribers} testId="stat-subscribers" />
                    <StatCard icon={ArrowUpRight} accent="sand" label="Reviews" value={totals.reviews} testId="stat-reviews" />
                    <StatCard
                        icon={FileText}
                        accent="coral"
                        label="Blog posts"
                        value={totals.blog_published}
                        sub={totals.blog_drafts ? `${totals.blog_drafts} draft` : "të gjithë publikuar"}
                        testId="stat-blog"
                    />
                </div>
            </div>
        </div>
    );
}
